import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { useSampleSize } from '~/hooks/useSampleSize'
import {
  exists,
  type FiveSegment,
  type FiveSegmentCounts,
  sumFiveNumbers,
} from '~/utils'

import { tabulationQueryDataAtom } from '../atoms'
import {
  type FiveSegmentWithAll,
  invertedOverlapKeyMap,
  type OverlapKey,
} from '../utils'

type OverlapCountData = {
  brand1: FiveSegmentWithAll
  brand2: FiveSegmentWithAll
  count: number
  ratio: number
}

export const useOverlapData = (): OverlapCountData[] => {
  const sampleSize = useSampleSize()

  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)

  return React.useMemo(() => {
    const overlap = tabulationQueryData?.overlap?.overlap
    if (!exists(overlap)) {
      return [{ brand1: 'all', brand2: 'all', count: sampleSize, ratio: 1 }]
    }

    const fiveSegmentMatrix = R.pipe(
      tabulationQueryData?.overlap?.overlap ?? {},
      R.entries(),
      R.map(([key, count]: [string, number]) => {
        const [brand1Key, brand2Key] = key.split('_x_') as [
          OverlapKey,
          OverlapKey,
        ]

        return {
          brand1: invertedOverlapKeyMap[brand1Key] as FiveSegment,
          brand2: invertedOverlapKeyMap[brand2Key] as FiveSegment,
          count,
        }
      }),
    )

    const brand1FiveSegmentCounts = R.pipe(
      fiveSegmentMatrix,
      R.groupBy(R.prop('brand2')),
      R.mapValues(R.sumBy(R.prop('count'))),
    ) as FiveSegmentCounts
    const brand2FiveSegmentCounts = R.pipe(
      fiveSegmentMatrix,
      R.groupBy(R.prop('brand1')),
      R.mapValues(R.sumBy(R.prop('count'))),
    ) as FiveSegmentCounts
    const total = sumFiveNumbers(brand1FiveSegmentCounts, 5, 4, 3, 2, 1)

    const fiveSegmentWithAllMatrix: Omit<OverlapCountData, 'ratio'>[] = [
      ...fiveSegmentMatrix,
      { brand1: 'all' as const, brand2: 'all' as const, count: total },
      ...R.entries(brand1FiveSegmentCounts).map(([brand2, count]) => ({
        brand1: 'all' as const,
        brand2: brand2 as FiveSegment,
        count,
      })),
      ...R.entries(brand2FiveSegmentCounts).map(([brand1, count]) => ({
        brand1: brand1 as FiveSegment,
        brand2: 'all' as const,
        count,
      })),
    ]

    return fiveSegmentWithAllMatrix.map((data) =>
      R.addProp(data, 'ratio', data.count / total),
    )
  }, [sampleSize, tabulationQueryData?.overlap?.overlap])
}
